import clsx from 'clsx';
import type { NivaasPackage } from '../types/package';
import { getPackageSupplementaryCost } from '../state/packages';
import { formatPrice } from '../state/offerings';

export interface PackageToggleProps {
  /** Currently selected package (null = room only) */
  selectedPackage: NivaasPackage | null;
  /** Callback when package selection changes */
  onSelectPackage: (pkg: NivaasPackage | null) => void;
  /** Available packages */
  packages: NivaasPackage[];
}

/**
 * PackageToggle - Compact inline toggle for package selection.
 * Shows a simple checkbox-style toggle for adding a package to the booking.
 * Designed to be unobtrusive and integrate smoothly into the booking flow.
 * Uses brand colors: green-200/300 (sage)
 */
export function PackageToggle({
  selectedPackage,
  onSelectPackage,
  packages,
}: PackageToggleProps) {
  if (packages.length === 0) return null;

  // For now, support single package toggle (most common case)
  // If multiple packages, show as dropdown or expand later
  const pkg = packages[0];
  const isSelected = selectedPackage?.id === pkg.id;

  const suppCost = getPackageSupplementaryCost(pkg);
  const additionalAmount = suppCost ? suppCost.amount + suppCost.gst : 0;

  const inclusions = pkg.supplementaryItems?.map((item) => item.name) ?? [];
  const inclusionsText = inclusions.slice(0, 3).join(', ');

  const handleToggle = () => {
    onSelectPackage(isSelected ? null : pkg);
  };

  return (
    <div
      className={clsx(
        'flex items-center justify-between py-3 px-4 rounded-lg cursor-pointer',
        'transition-all duration-150',
        isSelected
          ? 'bg-green-100 border border-green-300'
          : 'bg-gray-100 border border-gray-200 hover:border-gray-300',
      )}
      onClick={handleToggle}
      onKeyDown={(e) => e.key === 'Enter' && handleToggle()}
      role="checkbox"
      aria-checked={isSelected}
      tabIndex={0}
    >
      <div className="flex items-center gap-3">
        {/* Checkbox */}
        <div
          className={clsx(
            'w-5 h-5 rounded flex items-center justify-center flex-shrink-0 border-2',
            isSelected
              ? 'border-green-300 bg-green-300'
              : 'border-gray-400 bg-transparent',
          )}
        >
          {isSelected && (
            <svg width="12" height="12" viewBox="0 0 12 12" fill="none">
              <path
                d="M2 6L5 9L10 3"
                stroke="white"
                strokeWidth="2"
                strokeLinecap="round"
                strokeLinejoin="round"
              />
            </svg>
          )}
        </div>

        {/* Label */}
        <div>
          <div className="flex items-center gap-2">
            <span className="font-serif font-semibold text-sm text-gray-700">
              {pkg.name}
            </span>
            {additionalAmount > 0 && (
              <span
                className={clsx(
                  'text-sm font-mono font-medium',
                  isSelected ? 'text-green-300' : 'text-gray-500',
                )}
              >
                +{formatPrice({ amount: additionalAmount, currency: 'INR' })}
              </span>
            )}
          </div>
          {inclusionsText && (
            <span className="text-xs text-gray-500">
              Includes: {inclusionsText}
            </span>
          )}
        </div>
      </div>

      {/* Badge for selected state */}
      {isSelected && (
        <span className="text-xs font-serif font-semibold text-green-300 uppercase tracking-wider">
          Added
        </span>
      )}
    </div>
  );
}

/**
 * PackageToggleMultiple - For when there are multiple packages to choose from.
 * Shows as compact radio-style options.
 */
export function PackageToggleMultiple({
  selectedPackage,
  onSelectPackage,
  packages,
}: PackageToggleProps) {
  if (packages.length === 0) return null;

  return (
    <div className="flex flex-col gap-2">
      {/* Room Only option */}
      <PackageToggleOption
        label="Room Only"
        sublabel="Standard rate"
        isSelected={selectedPackage === null}
        onSelect={() => onSelectPackage(null)}
      />

      {/* Package options */}
      {packages.map((pkg) => {
        const suppCost = getPackageSupplementaryCost(pkg);
        const additionalAmount = suppCost ? suppCost.amount + suppCost.gst : 0;
        const inclusions = pkg.supplementaryItems?.map((item) => item.name).slice(0, 2).join(', ');

        return (
          <PackageToggleOption
            key={pkg.id}
            label={pkg.name}
            sublabel={inclusions || pkg.description}
            price={additionalAmount > 0 ? `+${formatPrice({ amount: additionalAmount, currency: 'INR' })}` : 'Included'}
            isSelected={selectedPackage?.id === pkg.id}
            onSelect={() => onSelectPackage(pkg)}
          />
        );
      })}
    </div>
  );
}

interface PackageToggleOptionProps {
  label: string;
  sublabel?: string;
  price?: string;
  isSelected: boolean;
  onSelect: () => void;
}

function PackageToggleOption({
  label,
  sublabel,
  price,
  isSelected,
  onSelect,
}: PackageToggleOptionProps) {
  return (
    <button
      type="button"
      onClick={onSelect}
      className={clsx(
        'flex items-center justify-between py-2.5 px-3.5 rounded-md w-full text-left',
        'transition-all duration-150 cursor-pointer',
        isSelected
          ? 'bg-green-100 border-2 border-green-300'
          : 'bg-white border border-gray-200 hover:border-gray-300',
      )}
    >
      <div className="flex items-center gap-2.5">
        {/* Radio */}
        <div
          className={clsx(
            'w-4 h-4 rounded-full border-2 flex items-center justify-center flex-shrink-0',
            isSelected ? 'border-green-300' : 'border-gray-400',
          )}
        >
          {isSelected && (
            <div className="w-2.5 h-2.5 rounded-full bg-green-300" />
          )}
        </div>

        <div>
          <span className="font-serif font-medium text-sm text-gray-700">
            {label}
          </span>
          {sublabel && (
            <span className="text-xs text-gray-500 ml-2">
              {sublabel}
            </span>
          )}
        </div>
      </div>

      {price && (
        <span
          className={clsx(
            'text-sm font-mono font-medium',
            isSelected ? 'text-green-300' : 'text-gray-500',
          )}
        >
          {price}
        </span>
      )}
    </button>
  );
}

export default PackageToggle;
